import React from "react";
import { Tractor, Microscope, ShieldCheck, Users, QrCode, Check, MoveRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHead } from "@/components/SectionHead";
import { TrustBar } from "@/components/TrustBar";
import { Reveal, Stagger, StaggerItem, ParallaxImage } from "@/lib/motion";
import { JOURNEY } from "@/data/content";
import { IMG } from "@/data/images";
import { useImg } from "@/lib/useImg";

const JOURNEY_IMAGES = [IMG.soilHands, IMG.heroFarm, IMG.heroExperience, IMG.heroStory, IMG.heroQuality, IMG.heroCollection, IMG.familyDinner];

const PEOPLE = [
  { icon: Tractor, title: "Our Farmers", text: "The heart of our journey. Growing with dedication and passion.", image: IMG.heroFarm },
  { icon: Microscope, title: "Our Experts", text: "Agronomists and nutrition experts who guide every step with knowledge.", image: IMG.lab },
  { icon: ShieldCheck, title: "Quality Professionals", text: "Committed to maintaining the highest standards every day.", image: IMG.moringaBowl },
  { icon: Users, title: "Dedicated Teams", text: "From processing to packaging, our teams work with care and responsibility.", image: IMG.heroCollection },
];

const TRACEABILITY = ["Product origin", "Harvest season (where available)", "Processing information", "Quality checks completed", "Nutritional information", "Recipes & usage ideas"];

export default function FarmToFamily() {
  const img = useImg();
  return (
    <div data-testid="farm-to-family-page">
      <PageHero
        tall
        eyebrow="From Farm to Family"
        lines={["Every Great Product Begins", "Long Before It Reaches", "Your Table."]}
        sub="Every Nutribest product begins with nature and passes through a carefully guided journey before becoming part of your family's healthy life."
        image={img("heroFarm")}
      />

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <SectionHead eyebrow="The Journey of Care" title="Seven steps. One promise." />
          <Stagger className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-6">
            {JOURNEY.map(({ icon: Icon, title, text }, i) => (
              <StaggerItem key={title}>
                <div data-testid={`care-step-${i + 1}`} className="text-center">
                  <span className="font-mono text-xs text-gold font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <div className="mt-2 mx-auto w-16 h-16 rounded-full border border-gold/50 bg-white flex items-center justify-center text-forest hover:bg-forest hover:text-cream transition-colors duration-500">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h4 className="mt-3 text-[11px] font-bold uppercase tracking-[0.14em] text-forest">{title}</h4>
                  <p className="mt-2 text-[11px] text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Stagger className="grid grid-cols-4 lg:grid-cols-7 gap-3 mt-12">
            {JOURNEY_IMAGES.map((src, i) => (
              <StaggerItem key={i}>
                <div className={`rounded-xl overflow-hidden h-28 lg:h-32 ${i > 3 ? "hidden lg:block" : ""}`}>
                  <img src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb grid lg:grid-cols-[1fr_1.6fr] gap-12">
          <Reveal className="flex flex-col justify-center">
            <p className="eyebrow mb-4">Behind every product is a story</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest leading-tight">The People Behind Your Nutrition</h2>
            <p className="mt-5 text-sm text-mutedgreen leading-relaxed">Nutribest is built by people who believe that quality begins with care.</p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 gap-5">
            {PEOPLE.map(({ icon: Icon, title, text, image }) => (
              <StaggerItem key={title}>
                <div data-testid={`people-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group bg-white rounded-2xl border border-forest/10 overflow-hidden hover:shadow-xl hover:shadow-forest/10 hover:-translate-y-1 transition-all duration-500">
                  <div className="h-40 overflow-hidden relative">
                    <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute bottom-3 left-3 w-9 h-9 rounded-full bg-forest text-cream flex items-center justify-center"><Icon size={15} /></span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-lg text-forest">{title}</h3>
                    <p className="mt-1.5 text-xs text-mutedgreen leading-relaxed">{text}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb grid lg:grid-cols-2 gap-8 items-stretch">
          <Reveal>
            <div className="h-full bg-forestdeep grain relative rounded-3xl p-8 sm:p-12">
              <p className="eyebrow !text-goldlight mb-4">Farm Partnership</p>
              <h3 className="font-serif text-3xl sm:text-4xl text-cream">Growing Together</h3>
              <p className="mt-4 text-cream/70 text-sm leading-relaxed">We believe strong relationships grow great quality.</p>
              <ul className="mt-8 space-y-3.5">
                {["Long-term partnerships built on trust", "Knowledge sharing and training support", "Encouraging responsible and regenerative farming", "Fair practices and mutual growth", "Uplifting communities, together"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-cream/85 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-gold flex items-center justify-center shrink-0"><Check size={11} strokeWidth={3} className="text-white" /></span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ParallaxImage src={img("heroExperience")} alt="Farmer and Nutribest agronomist in the field" className="rounded-3xl h-72 lg:h-full min-h-[300px]" speed={9} />
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-nb grid lg:grid-cols-2 gap-10 items-center bg-white border border-forest/10 rounded-3xl p-8 sm:p-12">
          <Reveal>
            <p className="eyebrow mb-4">Transparency you can see</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-forest">Know Your Product</h3>
            <p className="mt-4 text-sm text-mutedgreen leading-relaxed max-w-md">Scan the QR code on every Nutribest pack to discover the journey behind your product.</p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {TRACEABILITY.map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-xs sm:text-sm text-forest/85">
                  <MoveRight size={13} className="text-gold shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="flex justify-center">
            <div data-testid="traceability-phone" className="w-64 bg-forestdeep rounded-[2.2rem] p-3 shadow-2xl shadow-forest/30 rotate-2 hover:rotate-0 transition-transform duration-700">
              <div className="bg-cream rounded-[1.7rem] p-5 text-center">
                <QrCode size={72} strokeWidth={1.1} className="mx-auto text-forest" />
                <p className="font-serif text-forest mt-3">Moringa Leaf Powder</p>
                <p className="text-[10px] text-mutedgreen mt-1 uppercase tracking-[0.15em]">Batch NB-2026-0417</p>
                <div className="mt-4 space-y-1.5 text-left">
                  {["Origin: Karnataka, India", "Harvested: March 2026", "Quality Checked: Passed"].map((t) => (
                    <p key={t} className="text-[10px] text-mutedgreen flex items-center gap-1.5"><Check size={10} className="text-gold" /> {t}</p>
                  ))}
                </div>
                <div className="mt-4 bg-forest text-cream text-[10px] font-bold uppercase tracking-[0.15em] rounded-full py-2.5">View Details</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-nb grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-forest/10">
          <ParallaxImage src={img("familyDinner")} alt="Family at the dining table" className="h-72 lg:h-auto" speed={8} />
          <div className="bg-white p-8 sm:p-12 flex flex-col justify-center">
            <h3 className="font-serif text-3xl sm:text-4xl text-forest leading-tight">From Our Farms<br />To Your Family.</h3>
            <p className="mt-5 text-sm text-mutedgreen leading-relaxed max-w-md">We believe every step matters, because every family deserves food they never have to question.</p>
            <p className="mt-6 font-serif italic text-gold">Driven by Reliability.</p>
          </div>
        </div>
      </section>

      <TrustBar quote='"We hope to earn your trust."' />
    </div>
  );
}
